import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'Core/components';
import { selectFilteredPlans, selectChannels } from '../store/selectors';
import { updateProcess, fetchPlanner, fetchProcessCompletionStatus } from '../store/slice';

const STATUS_IN_PRODUCTION = 'In Production';
const STATUS_ON_HOLD = 'On Hold';

const getWorkTicketNumber = program =>
  String(
    program?.workTicketNumber ??
      program?.workTicketNo ??
      program?.workTicket ??
      program?.title ??
      '',
  ).replace(/^W+/i, '');

const getPartNumber = program => {
  const description = program?.description || program?.Description;
  if (description != null && String(description).trim() !== '') {
    return String(description).trim();
  }

  const direct =
    program?.partNumber ||
    program?.PartNumber ||
    program?.partNo ||
    program?.partCode ||
    program?.itemNumber;
  if (direct) return String(direct);

  const parts = Array.isArray(program?.processParts) ? program.processParts : [];
  const fromParts = parts
    .map(p => p?.partNumber || p?.PartNumber || p?.partNo || p?.partCode || p?.description)
    .filter(Boolean);
  return fromParts.length ? fromParts.join(', ') : '—';
};

const getCellNumber = (program, channels) => {
  const direct = program?.cellNumber ?? program?.CellNumber ?? program?.assetNumber;
  if (direct != null && String(direct).trim() !== '') {
    return String(direct).trim();
  }

  const channelId = String(program?.channelUuid ?? program?.machineId ?? '').trim();
  if (!channelId || !Array.isArray(channels)) return '';

  const match = channels.find(
    ch =>
      String(ch?.uuid ?? '').trim() === channelId ||
      String(ch?.id ?? '').trim() === channelId ||
      String(ch?.assetNumber ?? '').trim() === channelId,
  );
  if (!match) return channelId;
  return String(match.title || match.assetNumber || match.uuid || '').trim();
};

const OnOffToggle = ({ id, checked, disabled, onChange }) => (
  <label className="planner-on-off-toggle" htmlFor={id}>
    <input
      type="checkbox"
      id={id}
      checked={checked}
      disabled={disabled}
      onChange={e => onChange(e.target.checked)}
    />
    <span className="planner-on-off-toggle__track" aria-hidden="true">
      <span className="planner-on-off-toggle__text planner-on-off-toggle__text--on">On</span>
      <span className="planner-on-off-toggle__text planner-on-off-toggle__text--off">Off</span>
      <span className="planner-on-off-toggle__knob" />
    </span>
  </label>
);

OnOffToggle.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const WeekendOnOffModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const programs = useSelector(selectFilteredPlans);
  const channels = useSelector(selectChannels);
  const [searchText, setSearchText] = useState('');
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [optimisticStatus, setOptimisticStatus] = useState({});
  const [optimisticContinuousRun, setOptimisticContinuousRun] = useState({});

  const allRows = useMemo(() => {
    if (!Array.isArray(programs)) return [];
    return programs.map((program, index) => {
      const key = String(program?.id ?? getWorkTicketNumber(program) ?? index);
      const liveStatus = optimisticStatus[key] ?? program?.status ?? '';
      const continuousRun =
        optimisticContinuousRun[key] !== undefined
          ? !!optimisticContinuousRun[key]
          : program?.continuousRun === undefined
            ? false
            : !!program.continuousRun;
      return {
        key,
        workTicketNumber: getWorkTicketNumber(program) || '—',
        partNumber: getPartNumber(program),
        cellNumber: getCellNumber(program, channels),
        status: liveStatus,
        weekendOn: liveStatus === STATUS_IN_PRODUCTION,
        continuousRun,
        program,
      };
    });
  }, [programs, channels, optimisticStatus, optimisticContinuousRun]);

  const rows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const filtered = !q
      ? allRows
      : allRows.filter(row => {
          const part = String(row.partNumber || '').toLowerCase();
          const cell = String(row.cellNumber || '').toLowerCase();
          return part.includes(q) || cell.includes(q);
        });
    return filtered.map((row, index) => ({ ...row, serial: index + 1 }));
  }, [allRows, searchText]);

  const persistProgram = async (row, patch, revert) => {
    const saveKey = `${row.key}:${Object.keys(patch).join(',')}`;
    setSavingKeys(prev => new Set(prev).add(saveKey));
    try {
      await dispatch(
        updateProcess({
          ...row.program,
          ...patch,
        }),
      ).unwrap();
      await dispatch(fetchPlanner()).unwrap();
      await dispatch(fetchProcessCompletionStatus()).unwrap();
    } catch {
      revert();
    } finally {
      setSavingKeys(prev => {
        const next = new Set(prev);
        next.delete(saveKey);
        return next;
      });
    }
  };

  const handleWeekendToggle = async (row, nextChecked) => {
    const nextStatus = nextChecked ? STATUS_IN_PRODUCTION : STATUS_ON_HOLD;
    if (row.status === nextStatus) return;

    setOptimisticStatus(prev => ({ ...prev, [row.key]: nextStatus }));
    await persistProgram(row, { status: nextStatus }, () => {
      setOptimisticStatus(prev => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
    });
  };

  const handleContinuousRunToggle = async (row, nextChecked) => {
    if (row.continuousRun === nextChecked) return;

    setOptimisticContinuousRun(prev => ({ ...prev, [row.key]: nextChecked }));
    await persistProgram(row, { continuousRun: nextChecked }, () => {
      setOptimisticContinuousRun(prev => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
    });
  };

  const handleClose = () => {
    setSearchText('');
    setOptimisticStatus({});
    setOptimisticContinuousRun({});
    setSavingKeys(new Set());
    onClose();
  };

  const isSaving = (rowKey, field) => savingKeys.has(`${rowKey}:${field}`);

  const body = () => (
    <div className="weekend-on-off-modal">
      <div className="row">
        <div className="col-12 px-lg-5">
          <div className="w-100 d-flex row align-items-end justify-content-between flex-row mb-2">
            <div className="mb-3 col-6 col-lg-2">
              <div className="d-flex flex-column">
                <label className="mb-1 text-muted">Total Tasks</label>
                <h5 className="mb-0">{rows.length}</h5>
              </div>
            </div>
            <div className="mb-3 col-6 col-lg-2">
              <div className="d-flex flex-column">
                <label className="mb-1 text-muted">Weekend On</label>
                <h5 className="mb-0">{rows.filter(r => r.weekendOn).length}</h5>
              </div>
            </div>
            <div className="mb-3 col-6 col-lg-2">
              <div className="d-flex flex-column">
                <label className="mb-1 text-muted">Continuous Run On</label>
                <h5 className="mb-0">{rows.filter(r => r.continuousRun).length}</h5>
              </div>
            </div>
            <div className="mb-3 col-12 col-lg-6 d-flex justify-content-lg-end">
              <div className="weekend-on-off-modal__search">
                <input
                  id="weekend-on-off-search"
                  type="text"
                  className="form-control"
                  placeholder="Search by cell number or part number"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
                <i className="fas fa-search weekend-on-off-modal__search-icon" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="weekend-on-off-modal__content pt-4">
            {rows.length === 0 ? (
              <p className="text-muted mb-0 text-center py-5">
                {searchText.trim()
                  ? 'No tasks match your search.'
                  : 'No tasks found on the calendar.'}
              </p>
            ) : (
              <div className="weekend-on-off-modal__table-wrap">
                <table className="table table-hover mb-0 weekend-on-off-modal__table">
                  <thead>
                    <tr>
                      <th className="weekend-on-off-modal__col-serial">Serial No.</th>
                      <th className="weekend-on-off-modal__col-ticket">Work Ticket Number</th>
                      <th className="weekend-on-off-modal__col-part">Part Number</th>
                      <th className="weekend-on-off-modal__col-weekend text-center">Weekend</th>
                      <th className="weekend-on-off-modal__col-continuous text-center">
                        Continuous Run
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.key}>
                        <td className="weekend-on-off-modal__col-serial">{row.serial}</td>
                        <td className="weekend-on-off-modal__col-ticket">{row.workTicketNumber}</td>
                        <td className="weekend-on-off-modal__col-part" title={row.partNumber}>
                          {row.partNumber}
                        </td>
                        <td className="weekend-on-off-modal__col-weekend text-center">
                          <div className="d-flex justify-content-center py-1">
                            <OnOffToggle
                              id={`weekend-toggle-${row.key}`}
                              checked={row.weekendOn}
                              disabled={isSaving(row.key, 'status')}
                              onChange={checked => handleWeekendToggle(row, checked)}
                            />
                          </div>
                        </td>
                        <td className="weekend-on-off-modal__col-continuous text-center">
                          <div className="d-flex justify-content-center py-1">
                            <OnOffToggle
                              id={`continuous-run-toggle-${row.key}`}
                              checked={row.continuousRun}
                              disabled={isSaving(row.key, 'continuousRun')}
                              onChange={checked => handleContinuousRunToggle(row, checked)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      id="weekend-on-off-modal"
      show={show}
      size="xl"
      dialogClassName="weekend-on-off-modal-dialog"
      title="Weekend ON/OFF"
      body={body}
      footer={null}
      handleClose={handleClose}
    />
  );
};

WeekendOnOffModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default WeekendOnOffModal;
